import { inject, Injectable } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "@angular/fire/firestore";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { patch } from "@ngxs/store/operators";
import {
  cloudFunctionNames,
  ICheckIfEmailInUseRequest,
  ICheckIfEmailInUseResponse,
  IEntityAutoCollectExclusionMap,
  IEntityProgressMap,
  IGroupAutoCollectExclusionMap, Modify,
  UUID,
} from "src/app/core/interfaces";
import { defaultQuickStartState } from "src/app/core/utils/quick-start-helpers";
import { v4 as uuidV4 } from "uuid";
import {
  IEntity,
  IEntityMap,
  IGroup,
  IGroupMap,
  IHttpsCallableResponse,
  IMoveEntityGroupRequest,
  IOrganization,
  IUser,
  IUserInviteRequest,
  IUserInviteResponse,
  rolesToPermissions,
} from "src/app/core/interfaces";
import { ErrorHandlerService } from "src/app/core/services/firebase-error-handler.service";
import { updateGroupUsers } from "src/app/core/utils/common-db-operations";
import { EntityDetails } from "../entity-details-state/entity-details.actions";
import { OrganizationStateObject } from "./organization-model";
import { defaultSecureProState, SecureProStateModel } from "./secure-pro-model.interface";
import { Entity, Group, Organization, OrgUser } from "./secure-pro.actions";
import { User } from "../user-state/user.actions";
import { SecureProService } from "./secure-pro.service";

@State<SecureProStateModel>({
  name: "securePro",
  defaults: { ...defaultSecureProState },
})
@Injectable()
export class SecureProState {
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private functions = inject(Functions);
  private errorHandler = inject(ErrorHandlerService);
  private snackbar = inject(MatSnackBar);
  private secureProService = inject(SecureProService);

  @Selector()
  static getOrg(state: SecureProStateModel): OrganizationStateObject {
    return state.org;
  }

  @Selector()
  static getOrgId(state: SecureProStateModel): UUID {
    return state.org.id;
  }

  @Selector()
  static getOrgEntities(state: SecureProStateModel): IEntity[] {
    return state.org.entitiesList;
  }

  @Selector()
  static getEntity(state: SecureProStateModel) {
    return (entityId: UUID): IEntity | null => {
      return state.org.entitiesList.find((entity) => entity.id === entityId) ?? null;
    };
  }

  @Selector()
  static getIsEntityLoading(state: SecureProStateModel): boolean {
    return state.isEntityLoading;
  }

  @Selector()
  static getIsEntityOwnersLoading(state: SecureProStateModel): boolean {
    return state.isEntityOwnersLoading;
  }

  @Selector()
  static getIsEntityProgressLoading(state: SecureProStateModel): boolean {
    return state.isEntityProgressLoading;
  }

  @Selector()
  static getIsEntityTransferring(state: SecureProStateModel): boolean {
    return state.isEntityTransferring;
  }

  @Action(OrgUser.GetAllUsers)
  async getAllUsers(ctx: StateContext<SecureProStateModel>) {
    const org = ctx.getState().org;
    if (!org) {
      throw new Error("Organization does not exist!");
    }
    const orgId = org.id;

    try {
      // Use the organization ID to get all of the users in the organization
      // from firestore
      const userCollection = collection(this.firestore, "user");
      const userQuery = query(userCollection, where("orgId", "==", orgId));
      const userSnapshot = await getDocs(userQuery);
      const userData = userSnapshot.docs.map((doc) => {
        return {
          ...doc.data(),
          id: doc.id,
        } as Modify<IUser, { id: UUID }>;
      });
      // Patch state
      ctx.patchState({ orgUsers: userData });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(OrgUser.Update)
  async updateUser(
    ctx: StateContext<SecureProStateModel>,
    action: OrgUser.Update
  ) {
    const { updatedUser } = action;
    const org = ctx.getState().org;

    if (updatedUser.roles.includes("SuperAdmin")) {
      this.snackbar.open(
        "You cannot add another superadmin at this time.",
        "Dismiss",
        {
          duration: 5000,
        }
      );
      return;
    }

    // Now we can update the user
    const userModel: IUser = {
      ...updatedUser,
      orgId: org.id,
    };
    const userDoc = doc(this.firestore, "user", updatedUser.id);
    await setDoc(userDoc, <IUser>{
      ...userModel,
    });

    // Patch state
    const orgUsers = ctx.getState().orgUsers;
    const updatedOrgUsers: Modify<IUser, { id: UUID }>[] = orgUsers.map((user) => {
      if (user.id === updatedUser.id) {
        return {
          ...userModel,
          id: user.id,
        };
      }
      return user;
    });

    ctx.patchState({
      orgUsers: updatedOrgUsers,
    });
  }

  @Action(OrgUser.UpdateGroupMembership)
  async updateGroupMembership(
    ctx: StateContext<SecureProStateModel>,
    action: OrgUser.UpdateGroupMembership
  ) {
    const { userId, groupIds } = action;
    const stateGroups = ctx.getState().org.groups;
    // Update the state groups by adding the user id to the groups that the user
    // is a member of
    const updatedGroups = Object.keys(stateGroups).reduce((acc, groupId) => {
      const group = stateGroups[groupId];
      if (groupIds.includes(groupId)) {
        acc[groupId] = {
          ...group,
          userIds: Array.from(new Set([...group.userIds, userId])),
        };
      } else {
        // If the updated user is not a member of the group, we need to remove
        // the user id from the group if it exists
        acc[groupId] = {
          ...group,
          userIds: group.userIds.filter((id) => id !== userId),
        };
      }
      return acc;
    }, {} as IGroupMap);

    const org = ctx.getState().org;
    if (!org) {
      throw new Error("Organization does not exist!");
    }

    const updatedOrg: IOrganization = {
      ...org,
      groups: {
        ...updatedGroups,
      },
    };

    await updateGroupUsers(org.id, updatedGroups, this.firestore);

    // Update the org with the updated groups
    const orgDoc = doc(this.firestore, "organization", org.id);
    await updateDoc(orgDoc, {
      groups: updatedOrg.groups,
    });

    ctx.patchState({
      org: new OrganizationStateObject({
        ...updatedOrg,
        id: org.id,
      }),
    });
  }

  @Action(OrgUser.Delete)
  async deleteUser(
    ctx: StateContext<SecureProStateModel>,
    action: OrgUser.Delete
  ) {
    try {
      const userId = action.uid;
      const role = action.role;

      // Verify that the user isn't deleting himself
      if (this.auth.currentUser?.uid === userId) {
        this.snackbar.open("You cannot delete yourself!", "Dismiss", {
          duration: 5000,
        });
        return;
      }

      if (role === "SuperAdmin") {
        this.snackbar.open("You cannot delete the super admin!", "Dismiss", {
          duration: 5000,
        });
        return;
      }

      // Delete the user from the user collection
      const userDoc = doc(this.firestore, "user", userId);
      await deleteDoc(userDoc);

      // Delete the user from any groups that he's a member of
      const org = ctx.getState().org;
      const orgGroups = org.groups;
      const updatedGroups = Object.keys(orgGroups).reduce((acc, groupId) => {
        const group = orgGroups[groupId];
        if (group.userIds.includes(userId)) {
          acc[groupId] = {
            ...group,
            userIds: group.userIds.filter((id) => id !== userId),
          };
        } else {
          acc[groupId] = group;
        }
        return acc;
      }, {} as IGroupMap);

      // Update the org with the updated groups
      const updatedOrg: IOrganization = {
        ...org,
        groups: {
          ...orgGroups,
          ...updatedGroups,
        },
      };

      await updateGroupUsers(org.id, updatedGroups, this.firestore);

      // Patch state
      const orgUsers = ctx.getState().orgUsers;
      const updatedOrgUsers: Modify<IUser, { id: UUID }>[] = orgUsers.filter((user) => {
        return user.id !== userId;
      });

      ctx.patchState({
        org: new OrganizationStateObject({
          ...updatedOrg,
          id: org.id,
        }),
        orgUsers: updatedOrgUsers,
      });

      this.snackbar.open("User successfully deleted!", "Dismiss", {
        duration: 3000,
      });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(OrgUser.Reset)
  async resetOrgUser(ctx: StateContext<SecureProStateModel>) {
    ctx.setState({
      ...defaultSecureProState,
    });
  }

  @Action(OrgUser.InviteUser)
  async inviteUser(
    ctx: StateContext<SecureProStateModel>,
    action: OrgUser.InviteUser
  ) {
    const { email, role, groupIds } = action;
    const org = ctx.getState().org;

    // First, create the temp user doc in firestore
    try {
      if (role === "SuperAdmin") {
        this.snackbar.open(
          "You cannot add another superadmin at this time.",
          "Dismiss",
          {
            duration: 5000,
          }
        );
        return;
      }

      const checkIfEmailInUseCallable = httpsCallable<
        ICheckIfEmailInUseRequest,
        ICheckIfEmailInUseResponse
      >(this.functions, cloudFunctionNames.user.checkIfEmailInUse);

      const request: ICheckIfEmailInUseRequest = { email };

      const checkIfEmailInUseResponse = await checkIfEmailInUseCallable(request);
      const { success, message, isEmailInUse } = checkIfEmailInUseResponse.data;

      if (!success) {
        throw new Error(message)
      }

      if (isEmailInUse) {
        this.snackbar.open(
          "A user with that email already exists. Please use a different email.",
          "Dismiss",
          {
            duration: 5000,
          }
        );
        return;
      }

      const userCollection = collection(this.firestore, "user");
      const userDoc = doc(userCollection, uuidV4());
      const userInvite: IUser = {
        email,
        name: "(Pending)",
        consentFirstName: null,
        consentLastName: null,
        consentEmail: null,
        orgId: org.id,
        permissions: rolesToPermissions[role],
        roles: [role],
        skipTwoFactor: false,
        userType: "secure-pro",
        stripeCustomerId: null,
        quickStartState: defaultQuickStartState(),
        proAssociation: null,
      };
      await setDoc(userDoc, userInvite);

      // add the user id to all of the groups
      const updatedGroups = groupIds.reduce((acc, groupId) => {
        const group = org.groups[groupId];
        acc[groupId] = {
          ...group,
          userIds: Array.from(new Set([...group.userIds, userDoc.id])),
        };
        return acc;
      }, {} as IGroupMap);

      const updatedOrg: IOrganization = {
        ...org,
        groups: {
          ...org.groups,
          ...updatedGroups,
        },
      };

      await updateGroupUsers(org.id, updatedGroups, this.firestore);

      ctx.setState(
        patch<SecureProStateModel>({
          org: new OrganizationStateObject({
            ...updatedOrg,
            id: org.id,
          }),
        })
      );

      const inviteUserCallable = httpsCallable<
        IUserInviteRequest,
        IUserInviteResponse
      >(this.functions, cloudFunctionNames.user.sendUserInvite);

      const inviteResponse = await inviteUserCallable(<IUserInviteRequest>{
        to: email,
        orgId: org.id,
        orgName: org.name,
        tempUserId: userDoc.id,
        senderEmail: this.auth.currentUser?.email,
      });
      if (!inviteResponse.data.success) {
        throw new Error(inviteResponse.data.message);
      }
      this.snackbar.open(`Successfully invited ${email}!`, "Dismiss", {
        duration: 5000,
      });
      ctx.dispatch(new OrgUser.GetAllUsers());
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Organization.UpdateLogo)
  async updateOrganizationLogo(
    ctx: StateContext<SecureProStateModel>,
    action: Organization.UpdateLogo
  ) {
    const org = ctx.getState().org;
    const updatedOrg = {
      ...org,
      logoUrl: action.logoUrl,
    };
    ctx.patchState({
      org: new OrganizationStateObject(updatedOrg),
    });

    const orgDoc = doc(this.firestore, "organization", org.id);
    await updateDoc(orgDoc, { logoUrl: action.logoUrl });
  }

  @Action(Organization.UpdateName)
  async setOrganizationName(
    ctx: StateContext<SecureProStateModel>,
    action: Organization.UpdateName
  ) {
    const org = ctx.getState().org;
    const updatedOrg = {
      ...org,
      name: action.name,
    };
    ctx.patchState({
      org: new OrganizationStateObject(updatedOrg),
    });

    const orgDoc = doc(this.firestore, "organization", org.id);
    await updateDoc(orgDoc, { name: action.name });

    this.snackbar.open("Organization name updated!", "Dismiss", {
      duration: 5000,
    });
  }

  @Action(Organization.UpdateContactInformation)
  async updateOrganizationContacts(
    ctx: StateContext<SecureProStateModel>,
    action: Organization.UpdateContactInformation
  ) {
    const org = ctx.getState().org;

    const shouldUpdateContactInformation =
      action.orgPhone !== org.orgPhone || action.orgEmail !== org.orgEmail;

    if (!shouldUpdateContactInformation) return;

    const updatedOrg = {
      ...org,
      orgPhone: action.orgPhone,
      orgEmail: action.orgEmail,
    };
    ctx.patchState({
      org: new OrganizationStateObject(updatedOrg),
    });

    const orgDoc = doc(this.firestore, "organization", org.id);
    await updateDoc(orgDoc, {
      orgPhone: action.orgPhone,
      orgEmail: action.orgEmail,
    });

    this.snackbar.open("Organization contact information updated!", "Dismiss", {
      duration: 5000,
    });
  }

  @Action(Organization.UpdateEnterpriseFeaturesEnabled)
  async updateOrganizationEnterpriseFeaturesEnabled(
    ctx: StateContext<SecureProStateModel>,
    action: Organization.UpdateEnterpriseFeaturesEnabled
  ) {
    const org = ctx.getState().org;
    const updatedOrg = {
      ...org,
      enterpriseFeaturesEnabled: action.enterpriseFeaturesEnabled,
    };
    ctx.patchState({
      org: new OrganizationStateObject(updatedOrg),
    });

    const orgDoc = doc(this.firestore, "organization", org.id);
    await updateDoc(orgDoc, {
      enterpriseFeaturesEnabled: action.enterpriseFeaturesEnabled,
    });

    this.snackbar.open("Organization enterprise features enabled!", "Dismiss", {
      duration: 5000,
    });
  }

  @Action(Organization.Reset)
  async resetOrganization(ctx: StateContext<SecureProStateModel>) {
    ctx.setState({
      ...defaultSecureProState,
    });
  }

  @Action(Organization.Get)
  async getOrganization(
    ctx: StateContext<SecureProStateModel>,
    action: Organization.Get
  ) {
    const orgId = action.orgId;
    const orgDoc = doc(this.firestore, "organization", orgId);
    const orgSnapshot = await getDoc(orgDoc);
    if (orgSnapshot.exists()) {
      const orgData = orgSnapshot.data() as IOrganization;
      const groupDocs = await getDocs(
        collection(this.firestore, "organization", orgId, "groups")
      );
      const groups = groupDocs.docs.reduce((acc, doc) => {
        acc[doc.id] = doc.data() as IGroupMap[string];
        return acc;
      }, {} as IGroupMap);

      const fullOrg = {
        ...orgData,
        groups,
      };

      const orgState = new OrganizationStateObject({
        ...fullOrg,
        id: orgSnapshot.id,
      });
      // Patch state
      ctx.patchState({ org: orgState });
    } else {
      // Handle case where org does not exist
      console.error("Organization does not exist!");
    }
  }

  @Action(Organization.SetGlobalSelfBill)
  async setOrganizationGlobalSelfBill(
    ctx: StateContext<SecureProStateModel>,
    action: Organization.SetGlobalSelfBill
  ) {
    const org = ctx.getState().org;
    const updatedOrg = {
      ...org,
      selfBilled: action.selfBilled,
    };
    ctx.patchState({
      org: new OrganizationStateObject(updatedOrg),
    });

    const orgDoc = doc(this.firestore, "organization", org.id);
    await updateDoc(orgDoc, { selfBilled: action.selfBilled });

    this.snackbar.open(
      "Organization global self bill set to " + action.selfBilled,
      "Dismiss",
      {
        duration: 5000,
      }
    );
  }

  @Action(Organization.SetDefaultAutoCollect)
  async setOrganizationDefaultAutoCollect(
    ctx: StateContext<SecureProStateModel>,
    action: Organization.SetDefaultAutoCollect
  ) {
    const org = ctx.getState().org;
    const updatedOrg = {
      ...org,
      autoCollectOrgDefaultEnabled: action.defaultAutoCollect,
    };
    ctx.patchState({
      org: new OrganizationStateObject(updatedOrg),
    });

    const orgDoc = doc(this.firestore, "organization", org.id);
    await updateDoc(orgDoc, {
      autoCollectOrgDefaultEnabled: action.defaultAutoCollect,
    });

    this.snackbar.open(
      "Organization default Auto Collect set to " + action.defaultAutoCollect,
      "Dismiss",
      {
        duration: 5000,
      }
    );
  }

  @Action(Organization.UpdateCustomEmailContent)
  async updateCustomEmailContent(
    ctx: StateContext<SecureProStateModel>,
    action: Organization.UpdateCustomEmailContent
  ) {
    const org = ctx.getState().org;
    if (!org.enterpriseFeaturesEnabled) {
      this.snackbar.open(
        "Custom email content is an Enterprise Feature, Enterprise features are not enabled for this organization.",
        "Dismiss",
        {
          duration: 5000,
        }
      );
      return;
    }
    const updatedOrg = {
      ...org,
      customEmailContent: action.content,
    };
    ctx.patchState({
      org: new OrganizationStateObject(updatedOrg),
    });

    const orgDoc = doc(this.firestore, "organization", org.id);
    await updateDoc(orgDoc, { customEmailContent: action.content });

    this.snackbar.open("Organization custom email content updated", "Dismiss", {
      duration: 5000,
    });
  }
  /**
   *
   * Load all groups in the organization
   */
  @Action(Group.LoadAll)
  async loadAllGroups(ctx: StateContext<SecureProStateModel>) {
    const org = ctx.getState().org;
    if (!org) {
      throw new Error("Organization does not exist!");
    }

    const groupCollection = collection(
      this.firestore,
      "organization",
      org.id,
      "groups"
    );
    const groupSnapshot = await getDocs(groupCollection);
    const groups = groupSnapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data() as IGroup;
      return acc;
    }, {} as IGroupMap);

    ctx.patchState({
      org: new OrganizationStateObject({
        ...org,
        groups,
      }),
    });
  }
  /**
   *
   * Create or update a group in the organization
   */
  @Action(Group.Upsert)
  async upsertGroup(
    ctx: StateContext<SecureProStateModel>,
    action: Group.Upsert
  ) {
    const { groupId, group } = action;
    if (!group.name) throw new Error("Group name is required.");
    const org = ctx.getState().org;

    const groupBeforeUpdate: IGroup | undefined = org.groups[groupId];

    // When updating the group, we need to make sure that we move all of the entity
    // ids from the old group to the new group.
    // To do this, we can search through all of the groups in the organization
    // except the group we are updating, and if we find an entity id that matches
    // one in the updated group, we can remove it from the old group.
    const groupIds = Object.keys(org.groups);
    const otherGroupIds = groupIds.filter((id) => id !== groupId);

    const groupDoc = doc(
      this.firestore,
      "organization",
      org.id,
      "groups",
      groupId
    );

    let shouldUpdateGroup =
      !groupBeforeUpdate ||
      groupBeforeUpdate.entities.length !== group.entities.length ||
      groupBeforeUpdate.userIds.length !== group.userIds.length ||
      groupBeforeUpdate.name !== group.name;

    const groupEntitiesIds = group.entities.map((entity) => entity.id);
    groupBeforeUpdate?.entities.forEach((entity) => {
      if (!groupEntitiesIds.includes(entity.id)) shouldUpdateGroup = true;
    });

    groupBeforeUpdate?.userIds.forEach((userId) => {
      if (!group.userIds.includes(userId)) shouldUpdateGroup = true;
    });

    if (!shouldUpdateGroup) throw new Error("No changes to group.");

    await setDoc(
      groupDoc,
      {
        ...group,
      },
      { merge: true }
    );

    const moveEntityGroupCallable = httpsCallable<
      IMoveEntityGroupRequest,
      IHttpsCallableResponse
    >(this.functions, cloudFunctionNames.entity.moveEntityGroup);

    if (groupBeforeUpdate?.name !== group?.name) {
      const updateGroupNameInEntities = group.entities.map(
        async (publicEntity) => {
          const entityDoc = doc(
            this.firestore,
            "organization",
            org.id,
            "entityList",
            publicEntity.id
          );

          updateDoc(entityDoc, {
            groupName: group.name,
          });

          const entitySnapshot = await getDoc(entityDoc);
          const entity = entitySnapshot.data() as IEntity;

          if (entity.handedOff) {
            const secureFileDoc = doc(
              this.firestore,
              "secureFileEntities",
              entity.id
            );

            updateDoc(secureFileDoc, {
              groupName: group.name,
            });
          }
        }
      );

      await Promise.all(updateGroupNameInEntities);
    }

    // We're going to call the cloud function to update the entity groups
    for (const fromGroupId of otherGroupIds) {
      // Find each entity id that is in the updated group and isn't in
      // the group before the update
      const entitiesToRemoveFromOtherGroups = group.entities.filter(
        (transferredEntities) => {
          const transferredEntitiesNotInGroupBeforeUpdate =
            !groupBeforeUpdate?.entities
              .map((e) => e.id)
              .includes(transferredEntities.id);

          const fromGroupContainsTransferredEntity = org.groups[
            fromGroupId
          ].entities
            .map((e) => e.id)
            .includes(transferredEntities.id);

          return (
            transferredEntitiesNotInGroupBeforeUpdate &&
            fromGroupContainsTransferredEntity
          );
        }
      );

      for (const entity of entitiesToRemoveFromOtherGroups) {
        const moveEntityGroupData: IMoveEntityGroupRequest = {
          orgId: org.id,
          entityId: entity.id,
          entityLegalName: entity.name,
          fromGroupId: fromGroupId,
          toGroupId: groupId,
        };

        const moveEntityGroupResponse = await moveEntityGroupCallable(
          moveEntityGroupData
        );

        if (!moveEntityGroupResponse.data.success) {
          throw new Error(moveEntityGroupResponse.data.message);
        }
      }
    }

    const entityIdsToRemoveFromOtherGroups = group.entities.map(
      (entity) => entity.id
    );
    otherGroupIds.forEach((id) => {
      org.groups[id].entities = org.groups[id].entities.filter(
        (entity) => !entityIdsToRemoveFromOtherGroups.includes(entity.id)
      );
    });

    const updatedOrg: IOrganization = {
      ...org,
      groups: {
        ...org.groups,
        [groupId]: group,
      },
    };

    ctx.setState(
      patch<SecureProStateModel>({
        org: new OrganizationStateObject({
          ...updatedOrg,
          id: org.id,
        }),
      })
    );

    // Refresh the entity list
    ctx.dispatch(new Entity.GetEntities(Object.keys(updatedOrg.groups || {})));
  }

  /**
   * Deletes a group from the organization
   */
  @Action(Group.Delete)
  async deleteGroup(
    ctx: StateContext<SecureProStateModel>,
    action: Group.Delete
  ) {
    const { groupId } = action;
    const org = ctx.getState().org;
    if (!org) {
      throw new Error("Organization does not exist!");
    }

    // Can't delete the group if it's the default group
    if (org.groups[groupId].default) {
      this.snackbar.open("Cannot delete default group.", "Dismiss", {
        duration: 5000,
      });
      return;
    }

    // Can't delete the group if there are entities in it
    if (org.groups[groupId].entities.length > 0) {
      this.snackbar.open(
        "Cannot delete group with entities in it. Please transfer entities to another group first.",
        "Dismiss",
        {
          duration: 5000,
        }
      );
      return;
    }

    const updatedOrg: IOrganization = {
      ...org,
      groups: {
        ...org.groups,
      },
    };
    if (updatedOrg.groups && groupId in updatedOrg.groups) {
      delete updatedOrg.groups[groupId];
    }

    const groupDoc = doc(
      this.firestore,
      "organization",
      org.id,
      "groups",
      groupId
    );
    await deleteDoc(groupDoc);

    this.snackbar.open("Group successfully deleted!", "Dismiss", {
      duration: 3000,
    });
    ctx.patchState({
      org: new OrganizationStateObject({ ...updatedOrg, id: org.id }),
    });
  }

  @Action(Group.LoadAutoCollectExclusions)
  async loadGroupAutoCollectExclusions(
    ctx: StateContext<SecureProStateModel>
  ) {
    try {
      const org = ctx.getState().org;
      if (!org) {
        throw new Error("Organization does not exist!");
      }

      const groupAutoCollectExclusionsRef = collection(
        this.firestore,
        "organization",
        org.id,
        "autoCollectGroupExclusions"
      );

      const groupAutoCollectExclusionsSnapshot = await getDocs(
        groupAutoCollectExclusionsRef
      );

      const groupAutoCollectExclusions = groupAutoCollectExclusionsSnapshot.docs.reduce(
        (acc, doc) => {
          acc[doc.id] = doc.data() as { id: string };
          return acc;
        },
        {} as IGroupAutoCollectExclusionMap
      );

      ctx.dispatch(new Group.LoadAutoCollectExclusionsSuccess(groupAutoCollectExclusions));
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Group.LoadAutoCollectExclusionsSuccess)
  async loadGroupAutoCollectExclusionsSuccess(
    ctx: StateContext<SecureProStateModel>,
    action: Group.LoadAutoCollectExclusionsSuccess
  ) {
    try {
      const { groupAutoCollectExclusionMap } = action;
      const org = ctx.getState().org;
      ctx.patchState({
        org: new OrganizationStateObject({
          ...org,
          autoCollectGroupExclusions: groupAutoCollectExclusionMap,
        }),
      });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Group.AutoCollectExclusion)
  async autoCollectExclusion(
    ctx: StateContext<SecureProStateModel>,
    action: Group.AutoCollectExclusion
  ) {
    try {
      const { groupUpdate } = action;

      const org = ctx.getState().org;

      const batch = writeBatch(this.firestore);

      const groupAutoCollectExclusionsRef = collection(
        this.firestore,
        "organization",
        org.id,
        "autoCollectGroupExclusions"
      );

      if (groupUpdate.autoCollectExcluded) {
        const groupExclusionDoc = doc(groupAutoCollectExclusionsRef, groupUpdate.id);
        batch.set(groupExclusionDoc, { id: groupUpdate.id });
      } else {
        const groupExclusionDoc = doc(groupAutoCollectExclusionsRef, groupUpdate.id);
        batch.delete(groupExclusionDoc);
      }

      await batch.commit();

      ctx.dispatch(new Group.AutoCollectExclusionSuccess(groupUpdate));
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Group.AutoCollectExclusionSuccess)
  async autoCollectExclusionSuccess(
    ctx: StateContext<SecureProStateModel>,
    action: Group.AutoCollectExclusionSuccess
  ) {
    try {
      const { groupUpdate } = action;
      const groupAutoCollectExclusionMap = ctx.getState().org.autoCollectGroupExclusions || {};
      const updatedGroupAutoCollectExclusionMap = groupUpdate.autoCollectExcluded
        ? {
          ...groupAutoCollectExclusionMap,
          [groupUpdate.id]: { id: groupUpdate.id },
        }
        : Object.keys(groupAutoCollectExclusionMap).reduce((acc, id) => {
          if (id !== groupUpdate.id) {
            acc[id] = groupAutoCollectExclusionMap[id];
          }
          return acc;
        }, {} as IGroupAutoCollectExclusionMap);

      const org = ctx.getState().org;
      ctx.patchState({
        org: new OrganizationStateObject({
          ...org,
          autoCollectGroupExclusions: updatedGroupAutoCollectExclusionMap,
        }),
      });

      this.snackbar.open("Group auto collect exclusion updated successfully!", "Dismiss", {
        duration: 5000,
      });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////// ENTITY ACTIONS //////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Action(Entity.GetEntity)
  async getEntity(
    { dispatch, getState }: StateContext<SecureProStateModel>,
    { entityId }: Entity.GetEntity
  ) {
    const orgId = getState().org.id;
    try {
      const entity = await this.secureProService.getEntity(entityId, orgId);
      dispatch(new Entity.GetEntitySuccess(entity));
    } catch (error) {
      dispatch(new Entity.GetEntityFail(error));
    }
  }

  @Action(Entity.GetEntitySuccess)
  async getEntitySuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { entity }: Entity.GetEntitySuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [entity.id]: entity,
        },
      })
    });
  }

  @Action(Entity.GetEntityFail)
  async getEntityFail(
    { error }: Entity.GetEntityFail
  ) {
    this.errorHandler.handleError(error);
  }

  @Action(Entity.GetEntities)
  async getEntities(
    { dispatch, getState, patchState }: StateContext<SecureProStateModel>,
    { groupIds }: Entity.GetEntities
  ) {
    patchState({
      isEntityLoading: true,
    });
    try {
      const orgId = getState().org.id;
      const entities = await this.secureProService.getEntities(new Set(groupIds), orgId);
      dispatch(new Entity.GetEntitiesSuccess(entities));
    } catch (error) {
      dispatch(new Entity.GetEntitiesFail(error));
    }
  }

  @Action(Entity.GetEntitiesSuccess)
  async getEntitiesSuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { entities }: Entity.GetEntitiesSuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities
      }),
      isEntityLoading: false,
    })
  }

  @Action(Entity.GetEntitiesFail)
  async getEntitiesFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.GetEntitiesFail
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.Create)
  async createEntity(
    { dispatch, patchState }: StateContext<SecureProStateModel>,
    { entity }: Entity.Create
  ) {
    patchState({
      isEntityLoading: true,
    });
    const createEntityOperation = this.secureProService.createEntityOperation("createEntity")
    try {
      const newEntity = await createEntityOperation(entity);
      dispatch(new Entity.CreateSuccess(newEntity));
    } catch (error) {
      dispatch(new Entity.CreateFail(error));
    }
  }

  @Action(Entity.CreateSuccess)
  async createEntitySuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { entity }: Entity.CreateSuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [entity.id]: entity,
        },
      }),
      isEntityLoading: false,
    });
  }

  @Action(Entity.CreateFail)
  async createEntityFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.CreateFail
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.Update)
  async updateEntity(
    { dispatch, patchState }: StateContext<SecureProStateModel>,
    { entity }: Entity.Update
  ) {
    patchState({
      isEntityLoading: true,
    });
    const updateEntityOperation = this.secureProService.createEntityOperation("updateEntity")
    try {
      const updatedEntity = await updateEntityOperation(entity);
      dispatch(new Entity.UpdateSuccess(updatedEntity));
    } catch (error) {
      dispatch(new Entity.UpdateFail(error));
    }
  }

  @Action(Entity.UpdateSuccess)
  async updateEntitySuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { updatedEntity, message }: Entity.UpdateSuccess,
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [updatedEntity.id]: updatedEntity,
        },
      }),
      isEntityLoading: false,
    });
    if (message) {
      this.snackbar.open(message, "Dismiss", { duration: 5000 });
    }
  }

  @Action(Entity.UpdateFail)
  async updateEntityFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.UpdateFail,
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.AddEntityToGroup)
  async addEntityToGroup(
    { dispatch, patchState }: StateContext<SecureProStateModel>,
    { orgId, entityId, entityLegalName, groupId }: Entity.AddEntityToGroup
  ) {
    patchState({
      isEntityLoading: true,
    });
    try {
      const entity = await this.secureProService.addEntityToGroup(
        orgId,
        entityId,
        entityLegalName,
        groupId
      );
      dispatch(new Entity.AddEntityToGroupSuccess(entity));
    } catch (error: unknown) {
      dispatch(new Entity.AddEntityToGroupFail(error));
    }
  }

  @Action(Entity.AddEntityToGroupSuccess)
  async addEntityToGroupSuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { updatedEntity }: Entity.AddEntityToGroupSuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [updatedEntity.id]: updatedEntity,
        },
      }),
      isEntityLoading: false,
    });
  }

  @Action(Entity.AddEntityToGroupFail)
  async addEntityToGroupFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.AddEntityToGroupFail
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.MoveEntityToGroup)
  async moveEntityToGroup(
    { dispatch, patchState }: StateContext<SecureProStateModel>,
    { orgId, entityId, entityLegalName, fromGroupId, toGroupId }: Entity.MoveEntityToGroup
  ) {
    patchState({
      isEntityLoading: true,
    });
    try {
      const updatedEntity = await this.secureProService.moveEntityToGroup(
        orgId,
        entityId,
        entityLegalName,
        fromGroupId,
        toGroupId
      );
      dispatch(new Entity.MoveEntityToGroupSuccess(updatedEntity));
    } catch (error: unknown) {
      dispatch(new Entity.MoveEntityToGroupFail(error));
    }
  }

  @Action(Entity.MoveEntityToGroupSuccess)
  async moveEntityToGroupSuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { updatedEntity }: Entity.MoveEntityToGroupSuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [updatedEntity.id]: updatedEntity,
        },
      }),
      isEntityLoading: false,
    });
  }

  @Action(Entity.MoveEntityToGroupFail)
  async moveEntityToGroupFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.MoveEntityToGroupFail
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.AddOwnerToEntity)
  async addOwnerToEntity(
    { dispatch, getState, patchState }: StateContext<SecureProStateModel>,
    { entityId, ownerId, boiType }: Entity.AddOwnerToEntity
  ) {
    patchState({
      isEntityOwnersLoading: true,
    });
    const orgId = getState().org.id;
    const addOwnerToEntityOperation = this.secureProService.createEntityOperation("addOwnerToEntity")
    try {
      const entity = await addOwnerToEntityOperation(orgId, entityId, ownerId, boiType);
      dispatch(new Entity.AddOwnerToEntitySuccess(entity));
    } catch (error) {
      dispatch(new Entity.AddOwnerToEntityFail(error));
    }
  }

  @Action(Entity.AddOwnerToEntitySuccess)
  async addOwnerToEntitySuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { entity }: Entity.AddOwnerToEntitySuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [entity.id]: entity,
        },
      }),
      isEntityOwnersLoading: false,
    });
  }

  @Action(Entity.AddOwnerToEntityFail)
  async addOwnerToEntityFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.AddOwnerToEntityFail
  ) {
    patchState({
      isEntityOwnersLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.RemoveOwnerFromEntity)
  async removeOwnerFromEntity(
    { dispatch, getState, patchState }: StateContext<SecureProStateModel>,
    { entityId, ownerId, boiType }: Entity.RemoveOwnerFromEntity
  ) {
    patchState({
      isEntityOwnersLoading: true,
    });
    const orgId = getState().org.id;
    const removeOwnerFromEntityOperation = this.secureProService.createEntityOperation("removeOwnerFromEntity")
    try {
      const entity = await removeOwnerFromEntityOperation(orgId, entityId, ownerId, boiType);
      dispatch(new Entity.RemoveOwnerFromEntitySuccess(entity));
    } catch (error) {
      dispatch(new Entity.RemoveOwnerFromEntityFail(error));
    }
  }

  @Action(Entity.RemoveOwnerFromEntitySuccess)
  async removeOwnerFromEntitySuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { entity }: Entity.RemoveOwnerFromEntitySuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [entity.id]: entity,
        },
      }),
      isEntityOwnersLoading: false,
    });
  }

  @Action(Entity.RemoveOwnerFromEntityFail)
  async removeOwnerFromEntityFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.RemoveOwnerFromEntityFail
  ) {
    patchState({
      isEntityOwnersLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.GetTotalEntityProgress)
  async getTotalEntityProgress(
    { dispatch, getState, patchState }: StateContext<SecureProStateModel>,
    { entityId, ownerEntityIds, isNeedUpdateEntityInFirestore }: Entity.GetTotalEntityProgress
  ) {
    patchState({
      isEntityProgressLoading: true,
    });
    const orgId = getState().org.id;
    const getTotalEntityProgressOperation = this.secureProService.createEntityOperation("getTotalEntityProgress")
    try {
      const totalEntityProgress = isNeedUpdateEntityInFirestore
        ? await getTotalEntityProgressOperation(entityId, orgId)
        : await this.secureProService.getTotalEntityProgress(entityId, orgId);

      const ownerEntityTotalProgressPromises = ownerEntityIds.map(async (entityId) => {
        const { totalPercentFilled } = await getTotalEntityProgressOperation(entityId, orgId);
        return [entityId, totalPercentFilled] as [UUID, number];
      });
      const ownerEntityProgresses = await Promise.all(ownerEntityTotalProgressPromises);
      const ownerEntityProgressMap = ownerEntityProgresses.reduce((acc, [entityId, progress]) => {
        acc[entityId] = progress;
        return acc;
      }, {} as IEntityProgressMap);

      dispatch(new Entity.GetTotalEntityProgressSuccess(
        entityId,
        totalEntityProgress,
        ownerEntityProgressMap
      ));
    } catch (error) {
      dispatch(new Entity.GetTotalEntityProgressFail(error));
    }
  }

  @Action(Entity.GetTotalEntityProgressSuccess)
  async getTotalEntityProgressSuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { entityId, totalProgress, ownerEntityProgressMap }: Entity.GetTotalEntityProgressSuccess
  ) {
    try {
      const org = getState().org;
      const fullOwnerEntityProgressMap = {
        ...ownerEntityProgressMap,
        [entityId]: totalProgress.totalPercentFilled
      };

      if (!org.entities?.[entityId]) {
        throw new Error("Entity does not exist!");
      }

      const entities = org.entities ?? {};
      const updatedEntities = Object.keys(entities).reduce((acc, entityId) => {
        acc[entityId] = Object.prototype.hasOwnProperty.call(fullOwnerEntityProgressMap, entityId)
          ? {
            ...entities[entityId],
            progress: fullOwnerEntityProgressMap[entityId]
          }
          : entities[entityId]
        return acc;
      }, {} as IEntityMap);

      patchState({
        org: new OrganizationStateObject({
          ...org,
          entities: updatedEntities
        }),
        isEntityProgressLoading: false,
      });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Entity.GetTotalEntityProgressFail)
  async getTotalEntityProgressFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.GetTotalEntityProgressFail
  ) {
    patchState({
      isEntityProgressLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.TransferToSecureFile)
  async transferToSecureFile(
    { dispatch, getState, patchState }: StateContext<SecureProStateModel>,
    { entityId, ownerId }: Entity.TransferToSecureFile
  ) {
    patchState({
      isEntityTransferring: true,
    });
    const orgId = getState().org.id;
    const transferEntityToSecureFileOperation =
      this.secureProService.createEntityOperation("transferEntityToSecureFile")
    try {
      const entity = await transferEntityToSecureFileOperation(entityId, orgId, ownerId);
      dispatch(new Entity.TransferToSecureFileSuccess(entity));
    } catch (error: unknown) {
      dispatch(new Entity.TransferToSecureFileFail(error));
    }
  }

  @Action(Entity.TransferToSecureFileSuccess)
  async transferToSecureFileSuccess(
    { getState, patchState, dispatch }: StateContext<SecureProStateModel>,
    { transferredEntity }: Entity.TransferToSecureFileSuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [transferredEntity.id]: transferredEntity,
        },
      }),
      isEntityTransferring: false,
    });
    dispatch(new EntityDetails.UpdateWithEntityTransfer(transferredEntity))
  }

  @Action(Entity.TransferToSecureFileFail)
  async transferToSecureFileFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.TransferToSecureFileFail
  ) {
    patchState({
      isEntityTransferring: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.InstantlyTransferToSecureFile)
  async instantlyTransferToSecureFile(
    { patchState, getState, dispatch }: StateContext<SecureProStateModel>,
    { entityId, userId, email, displayName }: Entity.InstantlyTransferToSecureFile
  ) {
    patchState({
      isEntityTransferring: true,
    });
    const orgId = getState().org.id;
    const instantlyTransferEntityToSecureFileOperation =
      this.secureProService.createEntityOperation("instantlyTransferEntityToSecureFile")
    try {
      const entity = await instantlyTransferEntityToSecureFileOperation(entityId, orgId, userId, email, displayName);
      dispatch(new Entity.InstantlyTransferToSecureFileSuccess(entity));
    } catch (error: unknown) {
      dispatch(new Entity.InstantlyTransferToSecureFileFail(error));
    }
  }

  @Action(Entity.InstantlyTransferToSecureFileSuccess)
  async instantlyTransferToSecureFileSuccess(
    { getState, patchState, dispatch }: StateContext<SecureProStateModel>,
    { transferredEntity }: Entity.InstantlyTransferToSecureFileSuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [transferredEntity.id]: transferredEntity,
        },
      }),
      isEntityTransferring: false,
    });
    dispatch(new EntityDetails.UpdateWithInstantEntityTransfer(transferredEntity))
  }

  @Action(Entity.InstantlyTransferToSecureFileFail)
  async instantlyTransferToSecureFileFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.InstantlyTransferToSecureFileFail
  ) {
    patchState({
      isEntityTransferring: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.RevokeSecureFileInvite)
  async revokeSecureFileInvite(
    { dispatch, getState, patchState }: StateContext<SecureProStateModel>,
    { entityId }: Entity.RevokeSecureFileInvite
  ) {
    patchState({
      isEntityLoading: true,
    });
    const orgId = getState().org.id;
    try {
      const revokedEntity = await this.secureProService.revokeSecureFileInvitation(entityId, orgId);
      dispatch(new Entity.RevokeSecureFileInviteSuccess(revokedEntity));
    } catch (error: unknown) {
      dispatch(new Entity.RevokeSecureFileInviteFail(error));
    }
  }

  @Action(Entity.RevokeSecureFileInviteSuccess)
  async revokeSecureFileInviteSuccess(
    { getState, patchState, dispatch }: StateContext<SecureProStateModel>,
    { revokedEntity }: Entity.RevokeSecureFileInviteSuccess
  ) {
    const org = getState().org;
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: {
          ...org.entities,
          [revokedEntity.id]: revokedEntity,
        },
      }),
      isEntityLoading: false,
    });
    dispatch(new EntityDetails.Get(revokedEntity.id));
  }

  @Action(Entity.RevokeSecureFileInviteFail)
  async revokeSecureFileInviteFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.RevokeSecureFileInviteFail
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.Delete)
  async deleteEntity(
    { dispatch, getState, patchState }: StateContext<SecureProStateModel>,
    { entityId }: Entity.Delete
  ) {
    patchState({
      isEntityLoading: true,
    });
    const orgId = getState().org.id;
    const deleteEntityOperation = this.secureProService.createEntityOperation("deleteEntity")
    try {
      await deleteEntityOperation(entityId, orgId);
      dispatch(new Entity.DeleteSuccess(entityId));
    } catch (error) {
      dispatch(new Entity.DeleteFail(error));
    }
  }

  @Action(Entity.DeleteSuccess)
  async deleteEntitySuccess(
    { getState, patchState }: StateContext<SecureProStateModel>,
    { entityId }: Entity.DeleteSuccess
  ) {
    const org = getState().org;
    const updatedEntities = { ...org.entities };
    delete updatedEntities[entityId];
    patchState({
      org: new OrganizationStateObject({
        ...org,
        entities: updatedEntities,
      }),
      isEntityLoading: false,
    });
  }

  @Action(Entity.DeleteFail)
  async deleteEntityFail(
    { patchState }: StateContext<SecureProStateModel>,
    { error }: Entity.DeleteFail
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(Entity.SelfBill)
  async selfBillEntity(
    ctx: StateContext<SecureProStateModel>,
    action: Entity.SelfBill
  ) {
    try {
      const { entityUpdates } = action;

      const org = ctx.getState().org;

      const batch = writeBatch(this.firestore);

      const entityUpdatePromises = entityUpdates.map(async (entityUpdate) => {
        const entity = await org.fetchEntityById(entityUpdate.id);
        if (!entity) {
          throw new Error("Entity does not exist!");
        }

        const entityDoc = doc(
          this.firestore,
          "organization",
          org.id,
          "entityList",
          entity.id
        );

        if (!entityDoc) {
          throw new Error("Entity doc does not exist!");
        }

        batch.update(entityDoc, {
          selfBilled: entityUpdate?.selfBilled,
        });
      });
      await Promise.all(entityUpdatePromises);

      await batch.commit();

      if (org && org.entities) {
        const updatedEntities = { ...org.entities };

        entityUpdates.forEach(update => {
          updatedEntities[update.id] = {
            ...updatedEntities[update.id],
            selfBilled: update.selfBilled,
          };
        })

        ctx.patchState({
          org: new OrganizationStateObject({
            ...org,
            entities: updatedEntities
          }),
        });
      }

      ctx.dispatch(new Entity.SelfBillSuccess());
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Entity.LoadAutoCollectExclusions)
  async loadEntityAutoCollectExclusions(
    ctx: StateContext<SecureProStateModel>
  ) {
    try {
      const org = ctx.getState().org;
      if (!org) {
        throw new Error("Organization does not exist!");
      }

      const entityAutoCollectExclusionsRef = collection(
        this.firestore,
        "organization",
        org.id,
        "autoCollectEntityExclusions"
      );

      const entityAutoCollectExclusionsSnapshot = await getDocs(
        entityAutoCollectExclusionsRef
      );

      const entityAutoCollectExclusions = entityAutoCollectExclusionsSnapshot.docs.reduce(
        (acc, doc) => {
          acc[doc.id] = doc.data() as { id: string };
          return acc;
        },
        {} as IEntityAutoCollectExclusionMap
      );

      ctx.dispatch(new Entity.LoadAutoCollectExclusionsSuccess(entityAutoCollectExclusions));
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Entity.LoadAutoCollectExclusionsSuccess)
  async loadEntityAutoCollectExclusionsSuccess(
    ctx: StateContext<SecureProStateModel>,
    action: Entity.LoadAutoCollectExclusionsSuccess
  ) {
    try {
      const { entityAutoCollectExclusionMap } = action;
      const org = ctx.getState().org;
      ctx.patchState({
        org: new OrganizationStateObject({
          ...org,
          autoCollectEntityExclusions: entityAutoCollectExclusionMap,
        }),
      });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Entity.AutoCollectExclusion)
  async autoCollectExclusionEntity(
    ctx: StateContext<SecureProStateModel>,
    action: Entity.AutoCollectExclusion
  ) {
    try {
      const { entityUpdate } = action;

      const org = ctx.getState().org;

      const batch = writeBatch(this.firestore);

      const entityAutoCollectExclusionRef = collection(
        this.firestore,
        "organization",
        org.id,
        "autoCollectEntityExclusions"
      );

      if (entityUpdate.autoCollectExcluded) {
        const entityExclusionDoc = doc(entityAutoCollectExclusionRef, entityUpdate.id);
        batch.set(entityExclusionDoc, { id: entityUpdate.id });
      } else {
        const entityExclusionDoc = doc(entityAutoCollectExclusionRef, entityUpdate.id);
        batch.delete(entityExclusionDoc);
      }

      await batch.commit();

      ctx.dispatch(new Entity.AutoCollectExclusionSuccess(entityUpdate));
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Entity.AutoCollectExclusionSuccess)
  async autoCollectExclusionEntitySuccess(
    ctx: StateContext<SecureProStateModel>,
    action: Entity.AutoCollectExclusionSuccess
  ) {
    try {
      const { entityUpdate } = action;
      const entityAutoCollectExclusionMap = ctx.getState().org.autoCollectEntityExclusions || {};
      const updatedEntityAutoCollectExclusionMap = entityUpdate.autoCollectExcluded
        ? {
          ...entityAutoCollectExclusionMap,
          [entityUpdate.id]: { id: entityUpdate.id },
        }
        : Object.keys(entityAutoCollectExclusionMap).reduce((acc, id) => {
          if (id !== entityUpdate.id) {
            acc[id] = entityAutoCollectExclusionMap[id];
          }
          return acc;
        }, {} as IEntityAutoCollectExclusionMap);

      const org = ctx.getState().org;
      ctx.patchState({
        org: new OrganizationStateObject({
          ...org,
          autoCollectEntityExclusions: updatedEntityAutoCollectExclusionMap,
        }),
      });

      this.snackbar.open("Entity auto collect exclusion updated successfully!", "Dismiss", {
        duration: 5000,
      });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Entity.SelfBillSuccess)
  async selfBillEntitySuccess() {
    try {
      this.snackbar.open("Entity self bill updated successfully!", "Dismiss", {
        duration: 5000,
      });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }

  @Action(Entity.SubmitToFinCEN)
  async submitToFinCEN(
    { dispatch, getState }: StateContext<SecureProStateModel>,
    { entityId, consentData, isSubmittedByUser }: Entity.SubmitToFinCEN
  ) {
    const orgId = getState().org.id;

    try {
      const updatedEntity = await this.secureProService.submitToFinCEN(
        entityId,
        orgId,
        consentData
      );
      dispatch(new Entity.UpdateSuccess(updatedEntity));
      dispatch(new Entity.SubmitToFinCENSuccess(consentData, isSubmittedByUser));
    } catch (error: unknown) {
      dispatch(new Entity.SubmitToFinCENFail(error));
    }
  }

  @Action(Entity.SubmitToFinCENSuccess)
  async submitToFinCENSuccess(
    { dispatch }: StateContext<SecureProStateModel>,
    { consentData, isSubmittedByUser }: Entity.SubmitToFinCENSuccess
  ) {
    this.snackbar.open(
      "Filing process has started and will complete on its own, " +
      "please  go to \"View Filings\" to view records after filing",
      "Dismiss",
      { duration: 10000 }
    );

    if (isSubmittedByUser) {
      const { firstName, lastName, email } = consentData;
      dispatch(new User.UpdateConsentData(firstName, lastName, email));
    }
  }

  @Action(Entity.SubmitToFinCENFail)
  async submitToFinCENFail(
    { error }: Entity.SubmitToFinCENFail
  ) {
    this.errorHandler.handleError(error);
  }

  @Action(Entity.UpdateStripeSubscription)
  async updateStripeSubscription(
    ctx: StateContext<SecureProStateModel>,
    action: Entity.UpdateStripeSubscription
  ) {
    const org = ctx.getState().org;

    const batch = writeBatch(this.firestore);

    const entity = await org.fetchEntityById(action.entityId);

    if (!entity) {
      throw new Error("Entity does not exist!");
    }

    const entityDoc = doc(
      this.firestore,
      "organization",
      org.id,
      "entityList",
      entity.id
    );

    if (!entityDoc) {
      throw new Error("Entity doc does not exist!");
    }

    batch.update(entityDoc, {
      stripeSubscriptionId: action.subscriptionId,
    });

    if (org && org.entities) {
      ctx.patchState({
        org: new OrganizationStateObject({
          ...org,
          entities: {
            ...org.entities,
            [entity.id]: {
              ...org.entities[entity.id],
              stripeSubscriptionId: action.subscriptionId,
            },
          } as IEntityMap,
        }),
      });
    }

    await batch.commit();
  }

  @Action(Entity.UpdateCapTable)
  async updateCapTable(
    { patchState, dispatch }: StateContext<SecureProStateModel>,
    { entityId, orgId, shareTypeChanges }: Entity.UpdateCapTable
  ): Promise<void> {
    patchState({
      isEntityLoading: true,
    });

    const updateShareTypesOperation = this.secureProService.createEntityOperation("updateShareTypes");

    try {
      const updatedEntity = await updateShareTypesOperation(entityId, orgId, shareTypeChanges);

      dispatch(new Entity.UpdateSuccess(updatedEntity, "Share Types were successfully updated"));
    } catch (error) {
      dispatch(new Entity.UpdateFail(error));
    }
  }

  @Action(Entity.ManageCapTableRecords)
  async addCapTableRecord(
    { patchState, dispatch }: StateContext<SecureProStateModel>,
    { entityId, orgId, shareTypeId, shareRecordChanges }: Entity.ManageCapTableRecords
  ): Promise<void> {
    patchState({
      isEntityLoading: true,
    });

    const manageCapTableRecordsOperation =
      this.secureProService.createEntityOperation("manageCapTableRecords");

    try {
      const updatedEntity = await manageCapTableRecordsOperation(orgId, entityId, shareTypeId, shareRecordChanges);

      dispatch(new Entity.UpdateSuccess(updatedEntity, "Cap Table Records were successfully updated"));
    } catch (error) {
      dispatch(new Entity.UpdateFail(error));
    }
  }

  @Action(Entity.UpdateDocumentTypes)
  async updateDocumentTypes (
    { dispatch }: StateContext<SecureProStateModel>,
    { orgId, entityId, updatedDocTypes }: Entity.UpdateDocumentTypes
  ): Promise<void> {
    try {
      const updatedEntity = await this.secureProService.updateDocumentTypes(
        orgId,
        entityId,
        updatedDocTypes
      );

      dispatch(new Entity.UpdateSuccess(updatedEntity, "Document Types were successfully updated"));
    } catch (e) {
      dispatch(new Entity.UpdateFail(e));
    }
  }

  @Action(Entity.AddDocumentsToType)
  async addDocumentToType (
    { dispatch }: StateContext<SecureProStateModel>,
    { orgId, entityId, documentTypeId, documents }: Entity.AddDocumentsToType
  ): Promise<void> {
    try {
      const updatedEntity = await this.secureProService.addDocumentToType(
        orgId,
        entityId,
        documentTypeId,
        documents
      );

      dispatch(new Entity.UpdateSuccess(updatedEntity, "Documents were successfully added"));
    } catch (e) {
      dispatch(new Entity.UpdateFail(e));
    }
  }

  @Action(Entity.MoveDocumentBetweenTypes)
  async moveDocumentBetweenTypes (
    { dispatch }: StateContext<SecureProStateModel>,
    { orgId, entityId, fromDocumentTypeId, toDocumentTypeId, documentId }: Entity.MoveDocumentBetweenTypes
  ): Promise<void> {
    try {
      const updatedEntity = await this.secureProService.moveDocumentBetweenTypes(
        orgId,
        entityId,
        fromDocumentTypeId,
        toDocumentTypeId,
        documentId
      );

      dispatch(new Entity.UpdateSuccess(updatedEntity, "Document was successfully moved"));
    } catch (e) {
      dispatch(new Entity.UpdateFail(e));
    }
  }

  @Action(Entity.RemoveDocumentFromType)
  async removeDocumentFromType (
    { dispatch }: StateContext<SecureProStateModel>,
    { orgId, entityId, documentTypeId, document }: Entity.RemoveDocumentFromType
  ): Promise<void> {
    try {
      const updatedEntity = await this.secureProService.removeDocumentFromType(
        orgId,
        entityId,
        documentTypeId,
        document
      );

      dispatch(new Entity.UpdateSuccess(updatedEntity, "Document was successfully removed"));
    } catch (e) {
      dispatch(new Entity.UpdateFail(e));
    }
  }
}
